import React, { useEffect, useMemo, useRef, useState } from "react";

/* Creating a project, as a form rather than a conversation.
 *
 * The copilot used to ask for this in prose — "what should I call it? send the
 * name, an optional description, any links" — and that is the wrong shape for
 * structured input three fields wide. It makes the user compose a sentence,
 * makes the model parse it back out, and puts a guessed name one
 * misunderstanding away from a project they have to go and rename.
 *
 * A form removes the whole round trip: labelled boxes, a name they can see
 * before it exists, and one button. The model pre-fills whatever it heard, so
 * the common case ("create a project for the Canara bid") is already complete
 * and needs a single click.
 *
 * Only the name is shown at rest. Description and links exist, but a form that
 * opens with three empty boxes reads as a chore — they expand when wanted, or
 * automatically when the model already has something to put in them.
 */

const MAX_NAME = 255; // Folder.name is CharField(max_length=255)

const FolderPlusIcon = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.5h7A1.5 1.5 0 0 1 19 10v7.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 3 17.5z" />
    <path d="M11 13h4M13 11v4" />
  </svg>
);

const FIELD =
  "w-full rounded-lg border border-[#18101C]/12 bg-white px-3 py-2 text-[14.5px] text-[#18101C] " +
  "placeholder:text-[#77707E] transition-colors focus:border-[#A04A97] focus:outline-none " +
  "focus:ring-2 focus:ring-[#A04A97]/20";

const LABEL = "mb-1 block text-[12.5px] font-semibold text-[#6B6472]";

const CopilotProjectForm = ({ uid, initial, onCreate, onCancel }) => {
  const [name, setName] = useState(initial.name || "");
  const [description, setDescription] = useState(initial.description || "");
  const [links, setLinks] = useState(
    initial.web_links?.length ? initial.web_links : [""]
  );
  // Open only when the model already filled something in — otherwise the card
  // stays a single question.
  const [expanded, setExpanded] = useState(
    Boolean(initial.description || initial.web_links?.length)
  );

  const nameRef = useRef(null);
  useEffect(() => {
    // Focus only when there is nothing to read first. Stealing focus onto a
    // pre-filled field invites overtyping a name that was already right.
    if (!initial.name) nameRef.current?.focus();
  }, [initial.name]);

  const cleanName = name.trim();
  const valid = cleanName.length > 0 && cleanName.length <= MAX_NAME;

  const submit = () => {
    if (!valid) return;
    onCreate({
      name: cleanName,
      description: description.trim(),
      web_links: links.map((l) => l.trim()).filter(Boolean),
    });
  };

  const setLink = (i, v) => setLinks((prev) => prev.map((l, j) => (j === i ? v : l)));
  const addLink = () => setLinks((prev) => [...prev, ""]);
  const removeLink = (i) =>
    setLinks((prev) => (prev.length === 1 ? [""] : prev.filter((_, j) => j !== i)));

  const over = cleanName.length > MAX_NAME;

  return (
    <div className="px-4 py-3.5">
      <div>
        <label className={LABEL} htmlFor={`${uid}-name`}>
          Project name
        </label>
        <input
          id={`${uid}-name`}
          ref={nameRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="e.g. ABC Bank — Core Banking RFP"
          className={FIELD}
          aria-invalid={over || undefined}
        />
        {over && (
          <p role="alert" className="mt-1 text-[12.5px] text-[#B91C1C]">
            {cleanName.length} characters — keep it under {MAX_NAME}.
          </p>
        )}
      </div>

      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2.5 inline-flex items-center gap-1 rounded-md text-[13px] font-medium text-[#A04A97] transition-colors hover:text-[#8E4187] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
        >
          <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add description or links
        </button>
      ) : (
        <div className="mt-3 space-y-3 animate-copilot-rise motion-reduce:animate-none">
          <div>
            <label className={LABEL} htmlFor={`${uid}-desc`}>
              Description <span className="font-normal text-[#77707E]">— optional</span>
            </label>
            <input
              id={`${uid}-desc`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One line on what this bid is"
              className={FIELD}
            />
          </div>

          <div>
            <span className={LABEL}>
              Links <span className="font-normal text-[#77707E]">— optional</span>
            </span>
            <div className="space-y-1.5">
              {links.map((link, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <input
                    value={link}
                    onChange={(e) => setLink(i, e.target.value)}
                    placeholder="https://…"
                    className={FIELD}
                    aria-label={`Link ${i + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(i)}
                    aria-label={`Remove link ${i + 1}`}
                    className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-[#77707E] transition-colors hover:bg-[#18101C]/[0.05] hover:text-[#6B6472] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
                  >
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addLink}
              className="mt-1.5 inline-flex items-center gap-1 text-[13px] font-medium text-[#A04A97] transition-colors hover:text-[#8E4187] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
            >
              <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add link
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={!valid}
          className="rounded-lg bg-[#A04A97] px-3.5 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-[#8E4187] disabled:cursor-not-allowed disabled:bg-[#18101C]/15 disabled:text-[#77707E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
        >
          Create project
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[#18101C]/12 px-3.5 py-2 text-[13.5px] font-semibold text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.04] hover:text-[#3F3F46] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
        >
          Cancel
        </button>
        <span className="ml-auto text-[12px] text-[#77707E]">
          {valid ? "Nothing is created until you press Create" : "Give it a name to continue"}
        </span>
      </div>
    </div>
  );
};

export { FolderPlusIcon, MAX_NAME };
export default CopilotProjectForm;

/* Summary shown once the form has been submitted, in place of the fields. The
 * card never vanishes: what was created, and with what name, is part of the
 * record of the conversation. */
export const ProjectFormSummary = ({ values }) => {
  const links = useMemo(() => (values?.web_links || []).filter(Boolean), [values]);
  if (!values) return null;
  return (
    <div className="px-4 py-3.5">
      <p className="text-[14.5px] font-semibold leading-snug text-[#18101C]">{values.name}</p>
      {values.description && (
        <p className="mt-0.5 text-[13.5px] leading-relaxed text-[#6B6472]">{values.description}</p>
      )}
      {links.length > 0 && (
        <p className="mt-0.5 text-[13px] text-[#77707E]">
          {links.length} link{links.length === 1 ? "" : "s"}
        </p>
      )}
    </div>
  );
};
